/**
 * データ参照・レコード単位編集の列メタデータ
 * 設計: docs/design/v03-record-level-data-editing-detail.md §8
 *
 * - 表ヘッダは CSV の生列名 (menu_code) ではなく日本語ラベルを出す (Design HIGH-4)。
 *   CSV エクスポートは従来どおり生列名のままなので、往復整合には影響しない。
 * - モバイルではカードレイアウトへ切り替えるため、種別ごとに
 *   primary (見出し) / secondary (常時表示) / rest (折り畳み) を定義する (Design HIGH-5)。
 *   10列ある納品明細を機械的に縦積みすると10行のブロブになるため。
 */
export type FieldType = 'text' | 'number' | 'date' | 'month' | 'select';

export type FieldMeta = {
  name: string;
  label: string;
  type: FieldType;
  /** レコード単位編集で変更できるか。false = 表示のみ */
  editable: boolean;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  /** 数値を右寄せ・3桁区切りで表示するか */
  numeric?: boolean;
  placeholder?: string;
  hint?: string;
};

export const TAX_RATE_OPTIONS = [
  { value: '0.10', label: '10%（標準）' },
  { value: '0.08', label: '8%（軽減）' },
];

export const OPEX_ITEM_TYPES = [
  { value: 'rent', label: '賃料' },
  { value: 'electricity', label: '電気' },
  { value: 'gas', label: 'ガス' },
  { value: 'water', label: '水道' },
  { value: 'telephone', label: '通信' },
  { value: 'insurance', label: '保険' },
  { value: 'other', label: 'その他' },
];

/** 店舗は編集不可 (店舗を跨ぐ移動は権限スコープを跨ぐため、削除→追加で行う。設計 §4) */
const storeField: FieldMeta = { name: 'store_code', label: '店舗', type: 'text', editable: false };

export const FIELD_META: Record<string, FieldMeta[]> = {
  sales: [
    storeField,
    { name: 'menu_code', label: 'メニューコード', type: 'text', editable: true, required: true, hint: 'メニューを変えると単価・税率も取り直します' },
    { name: 'sales_month', label: '対象月', type: 'month', editable: true, required: true },
    { name: 'period_start', label: '期間開始', type: 'date', editable: true, required: true },
    { name: 'period_end', label: '期間終了', type: 'date', editable: true, required: true },
    { name: 'quantity', label: '販売数量', type: 'number', editable: true, required: true, numeric: true },
    { name: 'sales_amount', label: '売上金額（税込）', type: 'number', editable: true, required: true, numeric: true },
  ],
  delivery_sales: [
    storeField,
    { name: 'service_code', label: '業者コード', type: 'text', editable: true, required: true },
    { name: 'sales_month', label: '対象月', type: 'month', editable: true, required: true },
    { name: 'amount', label: '売上金額（税込）', type: 'number', editable: true, required: true, numeric: true },
    { name: 'tax_rate', label: '税率', type: 'select', editable: true, options: TAX_RATE_OPTIONS },
  ],
  delivery_details: [
    storeField,
    { name: 'delivery_date', label: '納品日', type: 'date', editable: true, required: true },
    { name: 'vendor_name', label: '業者名', type: 'text', editable: true },
    { name: 'delivery_slip_no', label: '納品書番号', type: 'text', editable: true },
    { name: 'ingredient_code', label: '食材コード', type: 'text', editable: true, required: true, hint: '食材を変えるとコスト分類も取り直します' },
    { name: 'unit_price', label: '単価', type: 'number', editable: true, numeric: true },
    { name: 'quantity', label: '数量', type: 'number', editable: true, numeric: true, hint: '返品はマイナスで入力します' },
    { name: 'amount', label: '金額（税抜）', type: 'number', editable: true, required: true, numeric: true },
    { name: 'tax_rate', label: '税率', type: 'select', editable: true, options: TAX_RATE_OPTIONS },
    { name: 'remarks', label: '備考', type: 'text', editable: true },
  ],
  labor: [
    storeField,
    { name: 'target_month', label: '対象月', type: 'month', editable: true, required: true },
    { name: 'labor_cost_amount', label: '人件費（税抜）', type: 'number', editable: true, required: true, numeric: true },
  ],
  opex: [
    storeField,
    { name: 'target_month', label: '対象月', type: 'month', editable: true, required: true },
    { name: 'item_type', label: '費目種別', type: 'select', editable: true, required: true, options: OPEX_ITEM_TYPES },
    { name: 'item_name', label: '費目名', type: 'text', editable: true },
    { name: 'amount', label: '金額（税抜）', type: 'number', editable: true, required: true, numeric: true, hint: '返金・値引き調整はマイナスで入力します' },
    { name: 'tax_rate', label: '税率', type: 'select', editable: true, options: TAX_RATE_OPTIONS },
  ],
  // --- 参照のみ (編集は /masters/* が担当) ---
  menus: [
    storeField,
    { name: 'menu_code', label: 'メニューコード', type: 'text', editable: false },
    { name: 'menu_name', label: 'メニュー名', type: 'text', editable: false },
    { name: 'menu_category_id', label: 'カテゴリID', type: 'text', editable: false },
    { name: 'unit_price', label: '単価（税込）', type: 'number', editable: false, numeric: true },
    { name: 'tax_rate', label: '税率', type: 'text', editable: false },
    { name: 'is_main_menu', label: '来客数計上', type: 'text', editable: false },
    { name: 'effective_from', label: '有効開始日', type: 'date', editable: false },
    { name: 'effective_to', label: '有効終了日', type: 'date', editable: false },
  ],
  kpi_thresholds: [
    storeField,
    { name: 'kpi_type', label: 'KPI種別', type: 'text', editable: false },
    { name: 'threshold_value', label: '閾値', type: 'number', editable: false, numeric: true },
    { name: 'direction', label: '方向', type: 'text', editable: false },
    { name: 'effective_from', label: '有効開始日', type: 'date', editable: false },
  ],
  stores: [
    { name: 'store_code', label: '店舗コード', type: 'text', editable: false },
    { name: 'store_name', label: '店舗名', type: 'text', editable: false },
    { name: 'address', label: '住所', type: 'text', editable: false },
    { name: 'effective_from', label: '有効開始日', type: 'date', editable: false },
    { name: 'effective_to', label: '有効終了日', type: 'date', editable: false },
    { name: 'status', label: '状態', type: 'text', editable: false },
  ],
  menu_categories: [
    { name: 'category_code', label: 'カテゴリコード', type: 'text', editable: false },
    { name: 'category_name', label: 'カテゴリ名', type: 'text', editable: false },
    { name: 'display_order', label: '表示順', type: 'number', editable: false, numeric: true },
    { name: 'effective_from', label: '有効開始日', type: 'date', editable: false },
    { name: 'effective_to', label: '有効終了日', type: 'date', editable: false },
  ],
  ingredient_master: [
    storeField,
    { name: 'ingredient_code', label: '食材コード', type: 'text', editable: false },
    { name: 'ingredient_name', label: '食材名', type: 'text', editable: false },
    { name: 'unit', label: '単位', type: 'text', editable: false },
    { name: 'unit_price', label: '単価（税抜）', type: 'number', editable: false, numeric: true },
    { name: 'cost_category_name', label: 'コスト分類', type: 'text', editable: false },
    { name: 'effective_from', label: '有効開始日', type: 'date', editable: false },
    { name: 'effective_to', label: '有効終了日', type: 'date', editable: false },
  ],
  delivery_services: [
    storeField,
    { name: 'service_code', label: '業者コード', type: 'text', editable: false },
    { name: 'service_name', label: '業者名', type: 'text', editable: false },
    { name: 'active_flg', label: '有効', type: 'text', editable: false },
    { name: 'effective_from', label: '利用開始日', type: 'date', editable: false },
    { name: 'effective_to', label: '利用終了日', type: 'date', editable: false },
  ],
};

/** モバイルカードの表示優先度。primary=見出し、secondary=常時表示、それ以外は折り畳み */
export const CARD_LAYOUT: Record<string, { primary: string; secondary: string[] }> = {
  sales:             { primary: 'menu_code',      secondary: ['sales_amount', 'quantity'] },
  delivery_sales:    { primary: 'service_code',   secondary: ['sales_month', 'amount'] },
  delivery_details:  { primary: 'ingredient_code', secondary: ['delivery_date', 'amount'] },
  labor:             { primary: 'target_month',   secondary: ['labor_cost_amount'] },
  opex:              { primary: 'item_name',      secondary: ['item_type', 'amount'] },
  menus:             { primary: 'menu_name',      secondary: ['menu_code', 'unit_price'] },
  kpi_thresholds:    { primary: 'kpi_type',       secondary: ['threshold_value', 'direction'] },
  stores:            { primary: 'store_name',     secondary: ['store_code', 'status'] },
  menu_categories:   { primary: 'category_name',  secondary: ['category_code', 'display_order'] },
  ingredient_master: { primary: 'ingredient_name', secondary: ['ingredient_code', 'cost_category_name'] },
  delivery_services: { primary: 'service_name',   secondary: ['service_code', 'active_flg'] },
};

export const fieldsOf = (dataType: string): FieldMeta[] => FIELD_META[dataType] ?? [];
export const editableFieldsOf = (dataType: string): FieldMeta[] => fieldsOf(dataType).filter((f) => f.editable);

/** 表示用の整形。税率が不明 (migration 0042 の backfill で確定できなかった行) は「不明」と出す */
export const formatCell = (field: FieldMeta, value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return field.name === 'tax_rate' ? '不明' : '−';
  }
  if (field.name === 'tax_rate') {
    const n = Number(value);
    return Number.isFinite(n) ? `${Math.round(n * 100)}%` : String(value);
  }
  if (field.numeric) {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString() : String(value);
  }
  return String(value);
};

/** 公開ゲートの影響期間を示す列名。data_type ごとに「どの列の月が公開単位か」が違う。
 *  ⚠️ apps/api/src/lib/data-record-schemas.ts の RECORD_TABLE_MAP.monthColumn と対応させること。 */
const PERIOD_FIELD: Record<string, string> = {
  sales: 'sales_month',
  delivery_sales: 'sales_month',
  delivery_details: 'delivery_date',
  labor: 'target_month',
  opex: 'target_month',
};

/** 行 (または編集中のフォーム値) から公開ゲートの影響期間を '2026年1月' 形式で導く。
 *  値が未入力・非対応種別なら null。 */
export const periodLabelOf = (dataType: string, row: Record<string, unknown>): string | null => {
  const field = PERIOD_FIELD[dataType];
  if (!field) return null;
  const raw = row[field];
  if (raw === null || raw === undefined || raw === '') return null;
  const [y, mo] = String(raw).slice(0, 7).replace(/\//g, '-').split('-');
  return y && mo ? `${y}年${Number(mo)}月` : null;
};
